import { skipToken, useInfiniteQuery } from '@tanstack/react-query';

import { type ClipsListResponse } from '@air/api';
import {
  type Board,
  type BoardsListOptions,
  type Clip,
  type ClipsListOptions,
  type ListResponse,
  type SortDirection,
  type SortField,
} from '@air/api/types';

import { GALLERY_ASSETS, GALLERY_BOARDS, GALLERY_FILES } from '~/constants/react-query-keys';
import { useAppliedSortField } from '~/hooks/search/useAppliedSortField';
import { useIsFlattenedView } from '~/hooks/search/useIsFlattenedView';
import { useSearchParams } from '~/hooks/search/useSearchParams';
import { useGalleryViewItems } from '~/swr-hooks/gallery/galleryView/useGalleryViewItems';
import { useBoardSearchParams } from '~/swr-hooks/search/useBoardSearchParams';
import { useClipSearchParams } from '~/swr-hooks/search/useClipSearchParams';

export type BoardsFetcherParams = {
  cursor: string | null;
  sortFieldName?: SortField['name'];
  sortDirection?: SortDirection;
  boardsFetchParams: Partial<BoardsListOptions>;
};

export type ClipsFetcherParams = {
  cursor: string | null;
  sortFieldName: SortField['name'];
  sortDirection: SortDirection;
  clipsFetchParams: Partial<ClipsListOptions>;
  expandNestedContent: boolean;
};

export type Fetchers<B extends Board, C extends Clip> = {
  boardsFetcher: (params: BoardsFetcherParams) => Promise<ListResponse<B>>;
  clipsFetcher: (params: ClipsFetcherParams) => Promise<ClipsListResponse<C>>;
  filesFetcher: (params: ClipsFetcherParams) => Promise<ClipsListResponse<C>>;
};

export type UseCollectionGalleryViewItemsParams<B extends Board, C extends Clip> = {
  fetchers: Fetchers<B, C>;
  enabled: boolean;
  cacheKeyParams: Record<string, unknown>;
  /**
   * When true, the hook treats `isFlattenedView` as `false` regardless of URL state.
   * Public callers set this from `lockedControls.expandNested` so a stale `?flat=true`
   * URL can't bypass the lock and fetch nested content.
   */
  isExpandNestedDisabled?: boolean;
};

// biome-ignore lint/plugin: pre-existing function declaration; out of scope for this selector swap
export function useCollectionGalleryViewItems<B extends Board, C extends Clip>({
  fetchers: { boardsFetcher, clipsFetcher, filesFetcher },
  enabled,
  cacheKeyParams,
  isExpandNestedDisabled = false,
}: UseCollectionGalleryViewItemsParams<B, C>) {
  const { appliedSortFieldDirection: sortDirection, appliedSortFieldName: sortFieldName } = useAppliedSortField();
  const clipsFetchParams = useClipSearchParams();
  const boardsFetchParams = useBoardSearchParams();
  const { search } = useSearchParams();
  const { isFlattenedView } = useIsFlattenedView({ disabled: isExpandNestedDisabled });

  const expandNestedContent = !!search || isFlattenedView;

  const dataCacheKey = {
    ...clipsFetchParams,
    ...boardsFetchParams,
    sortFieldName,
    sortDirection,
    expandNestedContent,
    ...cacheKeyParams,
  };

  const {
    isLoading: areBoardsLoading,
    data: boardsData,
    fetchNextPage: loadNextBoardPage,
    isFetchingNextPage: areBoardsValidating,
    hasNextPage: hasMoreBoards,
  } = useInfiniteQuery<ListResponse<B>>({
    queryKey: [GALLERY_BOARDS, dataCacheKey],
    queryFn:
      enabled && !isFlattenedView
        ? async ({ pageParam }) => {
            const cursor = pageParam && typeof pageParam === 'string' ? pageParam : null;

            return boardsFetcher({
              cursor,
              sortFieldName,
              sortDirection,
              boardsFetchParams,
            });
          }
        : skipToken,
    enabled: enabled && !isFlattenedView,
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor || null,
    initialPageParam: '',
  });

  // Flattened galleries never request boards, so clips must not wait on a boards page that will not arrive.
  const areAllBoardsLoaded = isFlattenedView || (!!boardsData && !hasMoreBoards);

  const {
    data: clipsData,
    fetchNextPage: loadNextClipsPage,
    isFetchingNextPage: areClipsValidating,
    isLoading: areClipsLoading,
    hasNextPage: hasMoreClips,
  } = useInfiniteQuery<ClipsListResponse<C>>({
    queryKey: [GALLERY_ASSETS, dataCacheKey],
    queryFn:
      enabled && sortFieldName && sortDirection
        ? ({ pageParam }) => {
            const cursor = pageParam && typeof pageParam === 'string' ? pageParam : null;

            return clipsFetcher({
              cursor,
              sortFieldName,
              sortDirection,
              clipsFetchParams,
              expandNestedContent,
            });
          }
        : skipToken,
    enabled: ({ state: { data } }) => !data || (areAllBoardsLoaded && enabled && !!sortFieldName && !!sortDirection),
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor || null,
    initialPageParam: '',
  });

  const areAllClipsLoaded = !!clipsData && !hasMoreClips;

  const {
    data: filesData,
    fetchNextPage: loadNextFilesPage,
    isFetchingNextPage: areFilesValidating,
    isLoading: areFilesLoading,
    hasNextPage: hasMoreFiles,
  } = useInfiniteQuery<ClipsListResponse<C>>({
    queryKey: [GALLERY_FILES, dataCacheKey],
    queryFn:
      enabled && sortFieldName && sortDirection
        ? ({ pageParam }) => {
            const cursor = pageParam && typeof pageParam === 'string' ? pageParam : null;

            return filesFetcher({
              cursor,
              sortFieldName,
              sortDirection,
              clipsFetchParams,
              expandNestedContent,
            });
          }
        : skipToken,
    enabled: ({ state: { data } }) =>
      !data || (areAllBoardsLoaded && areAllClipsLoaded && enabled && !!sortFieldName && !!sortDirection),
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor || null,
    initialPageParam: '',
  });

  const itemsData = useGalleryViewItems({
    boards: {
      isValidating: areBoardsLoading || areBoardsValidating,
      // The boards request is skipped while flattened, so this list stays empty and the gallery
      // does not reserve a boards section above assets.
      data: boardsData?.pages ?? [],
      loadNextPage: loadNextBoardPage,
      isVisible: !isFlattenedView,
      hasMore: !isFlattenedView && !!hasMoreBoards,
    },
    clips: {
      isValidating: areClipsLoading || areClipsValidating,
      data: clipsData?.pages ?? [],
      loadNextPage: loadNextClipsPage,
      isVisible: true,
      hasMore: !!hasMoreClips,
    },
    files: {
      isValidating: areFilesLoading || areFilesValidating,
      data: filesData?.pages ?? [],
      loadNextPage: loadNextFilesPage,
      isVisible: true,
      hasMore: !!hasMoreFiles,
    },
  });

  return itemsData;
}
